import { useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { type Feature, FeatureSchemas } from "@workspace/contracts/feature";
import { useState } from "react";
import { orpc } from "@/api/orpc";
import { DataTable } from "@/components/data-table/data-table";
import { DataTableActionBar } from "@/components/data-table/data-table-action-bar";
import { DataTableToolbar } from "@/components/data-table/data-table-toolbar";
import { CreateFeatureDialog } from "@/features/features/create";
import { DeleteFeature } from "@/features/features/delete";
import { UpdateFeatureDialog } from "@/features/features/update";
import { useFeaturesColumns } from "@/features/project/hooks/use-features-columns";
import { useDataTable } from "@/hooks/use-data-table";
import { Main } from "@/layout/main";
import { withDashboardPaginationDefaults } from "@/lib/pagination";
import type { DataTableRowAction } from "@/types/data-table";

export const Route = createFileRoute("/_authenticated/features")({
  validateSearch: (search) => FeatureSchemas.list.parse(withDashboardPaginationDefaults(search)),
  component: RouteComponent,
  loaderDeps: ({ search }) => search,
  loader: async ({ deps, context: { orpc, queryClient } }) => {
    await queryClient.ensureQueryData(orpc.features.list.queryOptions({ input: deps }));
  },
});

function RouteComponent() {
  const search = Route.useSearch();

  const [rowAction, setRowAction] = useState<DataTableRowAction<Feature> | null>(null);

  const columns = useFeaturesColumns({ setRowAction });

  const query = useSuspenseQuery(orpc.features.list.queryOptions({ input: search }));

  const { table } = useDataTable({
    columns: columns,
    data: query.data.items,
    pageCount: query.data.totalPageNumbers,
    debounceMs: 500,
    getRowId: (originalRow) => originalRow.id,
    enableSorting: true,
  });

  return (
    <Main>
      <DataTable table={table} actionBar={<DataTableActionBar table={table} />}>
        <DataTableToolbar table={table}>
          <CreateFeatureDialog />
        </DataTableToolbar>
      </DataTable>

      <DeleteFeature
        feature={rowAction?.row.original}
        open={rowAction?.variant === "delete"}
        onClose={() => setRowAction(null)}
      />

      <UpdateFeatureDialog
        feature={rowAction?.row.original}
        open={rowAction?.variant === "update"}
        onClose={() => setRowAction(null)}
      />
    </Main>
  );
}
